import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SEO = ({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schemaData,
  noindex = false
}) => {
  const siteName = 'Mindful Health Counseling';
  const defaultDescription = 'Professional parenting and family therapy services in Hawaii. Licensed mental health counselor specializing in child, adolescent, and family counseling in Wailuku, HI.';
  const defaultImage = 'https://customer-assets.emergentagent.com/job_aloha-parents/artifacts/we1ubgtn_file_00000000f90471f5a3205e77abce95f6.png';
  
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const metaDescription = description || defaultDescription;
  const metaImage = ogImage || defaultImage;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Keith Scherdt, LPC, LMHC" />
      <meta name="geo.region" content="US-HI" />
      <meta name="geo.placename" content="Wailuku" />
      <meta name="geo.position" content="20.8881;-156.5055" />
      <meta name="ICBM" content="20.8881, -156.5055" />

      {/* Schema.org Structured Data */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

// Schema.org data generators
export const getOrganizationSchema = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://mhcmaui.com');
  
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${baseUrl}/#organization`,
    "name": "Mindful Health Counseling",
    "url": baseUrl,
  "logo": "https://customer-assets.emergentagent.com/job_aloha-parents/artifacts/we1ubgtn_file_00000000f90471f5a3205e77abce95f6.png",
  "image": "https://customer-assets.emergentagent.com/job_aloha-parents/artifacts/we1ubgtn_file_00000000f90471f5a3205e77abce95f6.png",
  "description": "Professional parenting and family therapy services in Hawaii. Specializing in child, adolescent, and family counseling.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2200 Main Street",
    "addressLocality": "Wailuku",
    "addressRegion": "HI",
    "postalCode": "96793",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "20.8881",
    "longitude": "-156.5055"
  },
  "telephone": "+1-808-470-7650",
  "email": "keith@mhcmaui.com",
  "priceRange": "$$",
  "areaServed": {
    "@type": "State",
    "name": "Hawaii"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Mental Health Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalTherapy",
          "name": "Parenting Support"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalTherapy",
          "name": "Family Therapy"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "MedicalTherapy",
          "name": "Child & Adolescent Counseling"
        }
      }
    ]
  }
  };
};

export const getPersonSchema = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://mhcmaui.com');
  
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${baseUrl}/#person`,
  "name": "Keith Scherdt",
  "jobTitle": "Licensed Mental Health Counselor",
  "image": "https://customer-assets.emergentagent.com/job_aloha-parents/artifacts/we1ubgtn_file_00000000f90471f5a3205e77abce95f6.png",
  "description": "Licensed Mental Health Counselor specializing in parenting support, family therapy, and child/adolescent counseling in Hawaii.",
  "telephone": "+1-808-470-7650",
  "email": "keith@mhcmaui.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2200 Main Street",
    "addressLocality": "Wailuku",
    "addressRegion": "HI",
    "postalCode": "96793",
    "addressCountry": "US"
  },
  "alumniOf": [
    {
      "@type": "CollegeOrUniversity",
      "name": "Seton Hall University"
    },
    {
      "@type": "CollegeOrUniversity",
      "name": "Michigan State University"
    }
  ],
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "Licensed Professional Counselor (LPC)"
    },
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "license",
      "name": "Licensed Mental Health Counselor (LMHC)"
    }
  ]
  };
};

export const getServiceSchema = (serviceName, description, url) => ({
  "@context": "https://schema.org",
  "@type": "MedicalTherapy",
  "name": serviceName,
  "description": description,
  "url": url,
  "provider": {
    "@type": "Person",
    "name": "Keith Scherdt",
    "jobTitle": "Licensed Mental Health Counselor"
  },
  "areaServed": {
    "@type": "State",
    "name": "Hawaii"
  },
  "availableChannel": {
    "@type": "ServiceChannel",
    "serviceType": "Telehealth",
    "availableLanguage": "English"
  }
});

export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});
